/**
 * ЖИЛ в packages/brand-config/src/schema.ts. Переехал сюда, потому что zod обязан быть
 * объявлен ровно у одного пакета (правило R1, §Окружение 2), а схема на zod в brand-config
 * заставляла объявить его вторым. Вход `./schemas` описан матрицей ровно как «zod-схемы для
 * валидации тел intake/dispatch и ФАЙЛОВ КОНФИГУРАЦИИ» — это его прямое назначение.
 *
 * В `@smm/brand-config` остаётся то, ради чего он существует: чтение YAML, загрузка с диска
 * и `resolveChannelId` — единственный в репозитории выбор канала по профилю.
 */
/**
 * Форма `config/brands/<brand>.yaml`. Норма — 00-integration.md §0.1 и §Окружение 4
 * (правки к `02-contracts.md:785`, `:798–801`, `:799`).
 *
 * Обязательных полей четыре: `id`, `display_name`, `channels`, `publishing`. Остальные —
 * необязательные секции, приезжающие по одной вместе со своим блоком: `freshness` и `links`
 * (решения Ильи 25.08, `docs/PHASE-2.md` §2.1), `monitoring` (Б2), `knowledge` (Б1),
 * `generation` (Б3). Необязательность у всех пяти — одно и то же требование: конфиги фазы 1
 * обязаны продолжать грузиться, иначе публикатор падает на секции, которой не пользуется.
 * Отсутствие секции — законное состояние; требует её тот сервис, которому она нужна, при
 * старте и с текстом, называющим файл. Всё, чего нет в списке (`approvers`, `instructions`,
 * `sources`), не существует: `strictObject` отбивает их как опечатки, а не молча игнорирует.
 *
 * Обёртка `channels.<adapter>` — не украшение: канал публикации в архитектуре это адаптер,
 * следующими идут сайт и vc.ru. Плоское `telegram.*` пришлось бы ломать при первом же втором
 * адаптере.
 */

import * as z from 'zod';
import { BRAND_RE } from '../approval-key/limits.ts';
import { SOURCE_CODE_RE } from '../sources/source-code.ts';
import { dayKeyOf, wallMinuteToMs } from '../time.ts';
import { AUTHOR_OPINION_MAX_CODEPOINTS } from './author-opinion.ts';
import { KnowledgeRefsSchema } from './knowledge.ts';
import { PosterBrandSchema } from './poster.ts';

/**
 * Идентификатор канала Telegram. Пустая строка проходит схему НАМЕРЕННО: заготовку бренда
 * пишет bootstrap с пустыми каналами (§Окружение 6 шаг 9), и падение `ZodError`'ом на
 * `make setup` — ровно тот отказ, который эта заготовка убирает. Пустоту отбивает
 * `resolveChannelId` в момент разрешения канала, текстом для человека (§Окружение 5.1).
 */
export const TELEGRAM_CHAT_ID_RE = /^(-100\d{7,13})?$/;

export const TelegramChatId = z
  // Собственный текст на промах по типу — не украшение: `channel_id: -1001234567890` без
  // кавычек это по YAML 1.2 число, и это самая вероятная ручная правка файла. Дефолтный
  // «expected string, received number» ничего не говорит тому, кто заводил канал.
  .string({ error: 'id канала обязан быть строкой в кавычках: channel_id: "-100…"' })
  .regex(TELEGRAM_CHAT_ID_RE, 'ожидался id вида -100… (7–13 цифр после -100) или пустая строка')
  // Требование §Окружение 5.1. За текущей регуляркой не срабатывает никогда — проверено:
  // -100 плюс 13 девяток это ~1.01e15 против MAX_SAFE_INTEGER 9.007e15. Оставлено как замок
  // на случай расширения регулярки: id, потерявший младшие разряды в Number(), ушёл бы в
  // чужой канал молча.
  .refine(
    (v) => v === '' || Number.isSafeInteger(Number(v)),
    'id канала не помещается в безопасное целое',
  );

/**
 * ПРИВЯЗКА id К НАСТОЯЩЕМУ КАНАЛУ (2026-10-25, находка ревизии `no-channel-identity-binding`).
 *
 * Все замки docs/multi-brand.md §2 сверяют конфиг с конфигом: схема, непересечение каналов,
 * allowlist подписи и шаг 17 публикатора. Чужой `-100…`, вписанный в файл бренда одной
 * ошибкой вставки, проходит их все — бот один и администратор во всех каналах. Привязка
 * сверяет id с самим Telegram: публикатор на предстарте зовёт `getChat` по каждому каналу
 * каждого бренда и отказывается стартовать при расхождении (`CHANNEL_IDENTITY_MISMATCH`).
 *
 * Две формы, и только они:
 *   • `@username` — публичный канал: 5–32 символа `[A-Za-z0-9_]`. Сверяется без `@` и без
 *     учёта регистра — Telegram сам не различает регистр имени;
 *   • `title:<точное название>` — канал без имени (закрытый тестовый): 1–128 символов в одну
 *     строку, сверяется побайтно. Пробел сразу после двоеточия или в конце — отказ схемы, а
 *     не тихое «не совпало» на предстарте: такой привязки не выполнит ни один канал.
 *
 * В YAML значение пишется В КАВЫЧКАХ: `@` — зарезервированный символ YAML, и голое
 * `channel_ref: @geoboost` не разбирается вовсе.
 *
 * Привязка необязательна: без неё публикатор пишет наблюдённое имя и название в журнал
 * (`publisher.preflight.channel_identity`), и вписать их сюда можно, взяв оттуда.
 */
const CHANNEL_USERNAME_REF_RE = /^@([A-Za-z0-9_]{5,32})$/;
// Вне названия — управляющие символы \p{Cc} (в том числе \r, \n, табуляция) и переводы строки
// Unicode U+2028/U+2029: «в одну строку» значит без любого из них.
const CHANNEL_TITLE_REF_RE = /^title:(?!\s)([^\p{Cc}\u2028\u2029]{1,128})(?<!\s)$/u;

export type ChannelRef =
  | { readonly kind: 'username'; readonly username: string }
  | { readonly kind: 'title'; readonly title: string };

/** Разбор привязки. `null` — строка не привязка ни одной из двух форм. */
export function parseChannelRef(ref: string): ChannelRef | null {
  const u = CHANNEL_USERNAME_REF_RE.exec(ref);
  if (u !== null) return { kind: 'username', username: u[1] as string };
  const t = CHANNEL_TITLE_REF_RE.exec(ref);
  if (t !== null) return { kind: 'title', title: t[1] as string };
  return null;
}

/**
 * Совпадает ли привязка с тем, что вернул `getChat`. Одно правило на всех, кто сверяет
 * (публикатор на предстарте, инструменты выкладки): две копии сравнения разошлись бы
 * регистром или `@` первой же правкой.
 */
export function channelRefMatches(
  ref: ChannelRef,
  chat: {
    readonly username?: string | null | undefined;
    readonly title?: string | null | undefined;
  },
): boolean {
  if (ref.kind === 'username') {
    const seen = chat.username;
    return typeof seen === 'string' && seen.toLowerCase() === ref.username.toLowerCase();
  }
  return chat.title === ref.title;
}

export const ChannelRefSchema = z
  .string({ error: 'привязка канала обязана быть строкой в кавычках: "@username" или "title:…"' })
  .refine(
    (v) => parseChannelRef(v) !== null,
    'ожидалось "@username" (5–32 символа A–Z, a–z, 0–9, _) или "title:<точное название канала>" ' +
      '(1–128 символов в одну строку, без пробелов по краям)',
  );

/**
 * Множество зон строится один раз при загрузке модуля: `Intl.supportedValuesOf('timeZone')`
 * отдаёт 418 значений, и перебирать их на каждом разборе конфига незачем.
 *
 * `'UTC'` добавлен вручную. Проверено на Node 26.7.0: в перечислении нет ни `UTC`, ни `Etc/*` —
 * ни одного имени с нулевым смещением, — при том что `Intl.DateTimeFormat` зону `UTC` принимает,
 * а значит принимают её и `wallMinuteToMs`, и `dayKeyOf`.
 */
const TIME_ZONES: ReadonlySet<string> = new Set([...Intl.supportedValuesOf('timeZone'), 'UTC']);

/**
 * Канонизация именем самого Intl. Возвращает `null`, если имя вообще не зона.
 *
 * Проверка шире буквы §Окружение 4 («имя обязано быть в перечислении») ровно на один шаг:
 * имя принимается и тогда, когда в перечислении лежит его канонизированная форма.
 *
 * Причина — измеренная особенность V8, а не вкус. Проверено на Node 26.7.0: перечисление
 * отдаёт СТАРЫЕ имена (`Asia/Calcutta`, `Europe/Kiev`, `Africa/Asmera`), поэтому буквальная
 * проверка отбивала бы `Asia/Kolkata` и `Europe/Kyiv` — то есть ровно то написание, которое
 * человек и напишет сегодня, — с сообщением «неизвестная зона».
 *
 * Отбивается при этом всё, ради чего проверка существует: опечатка (`Europe/Moskva`) бросает
 * RangeError, а фиксированное смещение (`+03:00`) и `Etc/GMT+3` резолвятся сами в себя и в
 * перечисление не попадают.
 *
 * Принимается, следовательно, и написание регистром (`europe/moscow`), и алиас (`GMT`,
 * `Etc/UTC` → `UTC`) — это цена шага, а не отдельное решение. Поэтому имя не просто
 * проверяется, а заменяется на канонизированное: иначе два файла бренда несли бы два разных
 * написания одной зоны, и `publishing.timezone` в логе, в UI и в подсчёте `day_key`
 * выглядел бы по-разному у одного и того же пояса. После разбора значение всегда лежит в
 * `TIME_ZONES` — это утверждение теста.
 */
function canonicalTimeZone(tz: string): string | null {
  let resolved: string;
  try {
    resolved = new Intl.DateTimeFormat('en-US', { timeZone: tz }).resolvedOptions().timeZone;
  } catch {
    return null;
  }
  return TIME_ZONES.has(resolved) ? resolved : null;
}

export const IanaTimeZone = z
  .string()
  .refine(
    (tz) => canonicalTimeZone(tz) !== null,
    'неизвестная зона IANA (проверяется по перечислению Intl)',
  )
  // Трансформация исполняется только после успешного refine, поэтому `?? tz` недостижим;
  // он стоит здесь вместо `!`, который в репозитории запрещён.
  .transform((tz) => canonicalTimeZone(tz) ?? tz);

const SLOT_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Слот расписания. Своего `tz` у слота нет и быть не может: зона одна на бренд и лежит в
 * `publishing.timezone`. Два источника зоны — гарантированное расхождение материализации
 * слота (`wallMinuteToMs`) с подсчётом суток у публикатора (`dayKeyOf`).
 */
export const PublishingSlotSchema = z.strictObject({
  /** ISO-8601: 1 — понедельник, 7 — воскресенье. */
  weekday: z.int().min(1).max(7),
  /** «Стенное» время зоны бренда, `HH:MM`, минутная точность — как у `wallMinuteToMs`. */
  time: z.string().regex(SLOT_TIME_RE, 'ожидалось HH:MM (00:00–23:59)'),
});

/**
 * Предел возраста материала, в ЧАСАХ. `docs/sources.md` ПРАВИЛО 6.5: значение бренда, которое
 * никогда не выводится из кривой затухания ранжирования — это два разных числа.
 *
 * ЧТО ОНО ДЕЛАЕТ С 19.09.2026 — И ЧЕГО НЕ ДЕЛАЕТ (P2 ревизии 2026-10-18, находка 58). Возраст
 * при публикации НЕ проверяет никто: ни кнопка одобрения, ни подпись (`freshness: null`), ни
 * публикатор (docs/release-2026-09-19.md §6). Читает значение только мониторинг — как
 * запасной потолок свежести темы, когда у формата нет своего (`pipeline/rank.ts`, тема по
 * ссылке редактора), то есть оно задаёт срок на карточке темы, а не замок публикации.
 * Сквозной бюджет ПРАВИЛА 6.6 с ним сверяется предупреждением старта бэкенда
 * (`latencyBudgetProblem`), а не отказом загрузки.
 *
 * Диапазон 1..8760 часов выбран не на глаз: он ровно совпадает с окном, которое примет
 * демон подписи (`FRESHNESS_MIN_AGE_S = 3600`, `FRESHNESS_MAX_AGE_S = 31 536 000`,
 * approval-key/limits.ts). Шире — и конфиг разрешал бы значение, на котором подпись
 * отказывает кодом `FRESHNESS_INVALID`, то есть отказ приезжал бы не при загрузке файла,
 * а при первом одобрении с датой.
 *
 * Секция необязательна, и это компромисс, а не норма. Обязательной её сделать сегодня
 * нельзя: `strictObject` роняет старт бэкенда И публикатора на любом бренде без неё, а
 * заготовку бренда пишет bootstrap. Отсутствие секции означает `freshness: null` в
 * подписываемом payload, то есть проверку окна ВЫКЛЮЧЕННОЙ (шаг 23 публикатора). Полное
 * правило 6.6 (`max_gap + approval_sla + max_slot_wait ≤ max_age_at_publish`) приезжает
 * с Б2 вместе с остальными двумя слагаемыми — до тех пор проверяемо только это одно поле.
 */
export const FreshnessSchema = z.strictObject({
  max_age_at_publish_hours: z.int().min(1).max(8760),
  /**
   * Порог свежести — свойство ФОРМАТА, а не скаляр бренда (`docs/sources.md`
   * ПРАВИЛО 6.10): `null` — формат вечнозелёный, освобождён от проверки; число —
   * потолок возраста в часах. До библиотеки форматов (Б1.5) таблица двухэлементная —
   * `{ news: 48, evergreen: null }` (01-monitoring §10.3), и `topic` получает от неё
   * метку правила. Секция необязательна: конфиги фазы 1 живут без неё.
   */
  formats: z.record(z.string().min(1), z.union([z.int().min(1).max(8760), z.null()])).optional(),
});

/**
 * Секция мониторинга бренда — дельта §13 `docs/phase-2/01-monitoring.md`.
 * Необязательна: бренд без мониторинга обязан продолжать грузиться, иначе публикатор
 * упадёт на конфиге, которым не пользуется.
 *
 * Ни одна константа времени конвейера не имеет права быть литералом (`docs/sources.md`
 * §6): всё, что здесь, либо выводится из `schedule`, либо доказуемо от него не зависит.
 */
export const MonitoringSchema = z.strictObject({
  enabled: z.boolean().default(false),
  /** cron, 5 полей; зона исполнения — publishing.timezone (ПРАВИЛО 6.21). */
  schedule: z.string().min(1),
  /** ПОЛ холодного старта, НЕ окно (ПРАВИЛО 6.2): окно сбора живёт на курсоре. */
  lookback_hours: z.int().positive(),
  /**
   * Потолок догона: разрыв больше — фид на всю глубину плюс алерт (ПРАВИЛО 6.2).
   *
   * НЕ ЧИТАЕТСЯ НИЧЕМ, и это осознанно (сверено по коду 2026-09-07,
   * `services/agent-monitor/src/run.ts:588`): догон по ПРАВИЛУ 6.2 НЕ обрезается — окно
   * сбора живёт на курсоре и берётся целиком, каким бы длинным ни был простой. Ручка
   * оставлена объявленной, а не удалена, потому что удаление сломало бы конфиги, где она
   * уже написана; провода к ней не тянут намеренно: реализованная как настоящая обрезка,
   * она молча теряла бы материал за пропущенные сутки. По той же причине алерт
   * `catch_up_truncated` стоит в списке исключений реестра (`alerts/registry.test.ts`):
   * сработать ему не от чего.
   */
  catch_up_max_hours: z.int().positive().default(336),
  /** Память дедупа от терминального решения, полуинтервал (ПРАВИЛО 6.19). */
  dedup_window_days: z.int().positive().default(56),
  /** Единственное определение мёртвого источника — время (ПРАВИЛО 6.14). */
  dead_source_after_days: z.int().positive().default(14),
  /** Потолок прогона; по исчерпании обход останавливается на границе источника (О-3). */
  run_timeout_minutes: z.int().positive(),
  /** Языки материалов; вне списка — отсев стадии 0 (§8.1 шаг 6). */
  languages: z.array(z.string().regex(/^[a-z]{2}$/)).min(1),
  min_material_chars: z.int().positive().default(300),
  /**
   * Потолок выжимки на материал. 6000 — АБСОЛЮТНЫЙ потолок схемы БД (CHECK из 0002);
   * конфиг может только опустить его, поднять — новой миграцией (вопрос №2 Илье).
   */
  evidence_excerpt_max: z.int().positive().max(6000).default(6000),
  /** ПРАВИЛО Ф-1: лексикон — только положительное решение, пока не измерены ложные отсевы. */
  lexicon_is_gate: z.boolean().default(false),
  /** Полураспад свежести долей номинального окна (§10.3). Дефолт §13 — 0.35. */
  half_life_ratio: z.number().gt(0).lte(1).default(0.35),
  approval_sla_hours: z.int().positive(),
  generation_lead_hours: z.int().positive(),
  /**
   * РУЧКА ОТКАТА для ссылки, которую редактор вставляет в диалоге ручного прогона
   * (правка 5 от 2026-09-08, §5.1). До неё введённый адрес был ПОЖЕЛАНИЕМ К ТЕМЕ и никогда
   * не выкачивался: обход идёт по реестру. Теперь он выкачивается — а значит появляется
   * исходящий запрос на хост, которого не разрешал ни один конфиг.
   *
   * `any-public` (умолчание) — ходить по любому ПУБЛИЧНОМУ https-хосту. Иначе правка не
   * решает поставленную задачу вовсе: человек вставляет ссылку на что угодно, и требовать
   * от него сначала завести источник в реестре — это тот же отказ, только вежливый.
   * `registry` — прежнее поведение: адрес обязан принадлежать зарегистрированному
   * источнику. Одна строка конфига возвращает всё назад, без выкладки кода.
   *
   * ПРАВИЛО О-4 ЭТИМ НЕ НАРУШАЕТСЯ. О-4 — про ИСТОЧНИКИ: источнику можно ходить только по
   * своим доменам, и `allowedHostsOf` это исполняет. Адрес, введённый аутентифицированным
   * редактором в панели, — не источник. Ограда против SSRF при этом не в конфиге, а в коде
   * выборки и не отключается ничем: только `https`, адрес резолвится и проверяется ДО
   * запроса и ЗАНОВО на КАЖДОМ редиректе, непубличные диапазоны (приватные, loopback,
   * link-local, multicast) — отказ, редиректы/тело/таймаут — из умолчаний реестра, хост
   * пишется в журнал прогона.
   */
  operator_url_hosts: z.enum(['any-public', 'registry']).default('any-public'),
  /**
   * ОХВАТ ОБХОДА — четыре ручки AGI Boardroom (решение Ильи 2026-11-02: «ни один источник не
   * может быть пропущен», а сто с лишним источников бренда последовательный обход под
   * восьмиминутным потолком не успевает). Все четыре НЕОБЯЗАТЕЛЬНЫ и без умолчания в схеме:
   * конфиг без них разбирается в тот же объект, что и до правки, и мониторинг GeoBoost не
   * меняется ни на байт. Умолчания подставляют читатели (`crawlConcurrencyOf` и соседи ниже).
   *
   * Сколько источников обходить одновременно; 1 (умолчание) — сегодняшний последовательный
   * обход. Больше — параллельно, но не больше одного запроса на хост и браузерные выборки по
   * одной; РЕЗУЛЬТАТЫ — в порядке реестра: дедуп стадии 0 «первый в файле побеждает» обязан
   * остаться детерминированным. Потолок 12 — вежливость к чужим сайтам и память дроплета.
   */
  crawl_concurrency: z.int().min(1).max(12).optional(),
  /**
   * Потолок прогона по РАСПИСАНИЮ, когда его никто не ждёт (новость и дайджест автоматики), в
   * минутах. Нет ключа — тот же потолок, что у кнопки (`MANUAL_RUN_TIMEOUT_MS` мониторинга,
   * 8 мин). Кнопку ключ не трогает: её ждёт писатель не дольше 12 минут. Верх 25 — уборка
   * бэкенда закрывает заявку, взятую дольше 25 минут (`MONITORING_TAKEN_STALE_MS`).
   */
  scheduled_run_timeout_minutes: z.int().min(1).max(25).optional(),
  /**
   * Суждённые релевантными, но не выбранные кандидаты прогона по расписанию НЕ отчитываются
   * виденными материалами — и завтрашний прогон вправе их взять (в своём окне). Нерелевантные
   * отчитываются как сегодня. Нет ключа — `false`: всё суждённое попадает в дедуп, как раньше.
   */
  rejudge_unselected: z.boolean().optional(),
  /**
   * Потолки отбора новостного прогона вместо констант мониторинга: `max` — кандидатов стадии 1
   * (`MANUAL_LLM_CANDIDATES_MAX`, 100), `per_source` — квота источника в срезе
   * (`NEWS_PER_SOURCE_MAX`, 8), `deep` — вызовов стадии 2 (`NEWS_DEEP_MAX`, 25). Тройка — одно
   * решение: квота и глубина подбираются под ширину среза, поэтому ключи блока обязательны все.
   * Нет блока — константы.
   */
  candidates: z
    .strictObject({
      max: z.int().min(10).max(400),
      per_source: z.int().min(1).max(50),
      deep: z.int().min(5).max(100),
    })
    .check((ctx) => {
      // Квота источника и глубина больше ширины среза — ручки, которые не действуют: срез их
      // всё равно обрежет, а читающий конфиг поверит числу.
      for (const key of ['per_source', 'deep'] as const) {
        if (ctx.value[key] > ctx.value.max) {
          ctx.issues.push({
            code: 'custom',
            input: ctx.value,
            path: [key],
            message: `${key} ${String(ctx.value[key])} больше max ${String(ctx.value.max)}: срез из max кандидатов столько не даст`,
          });
        }
      }
    })
    .optional(),
});

// ---------------------------------------------------------------------------
// Симуляция расписания (docs/sources.md ПРАВИЛО 6.21): max_gap считается прогоном
// не менее 400 суток срабатываний, а не «взять интервал из выражения» —
// "0 12 * * 2,4" даёт разрыв 5 суток, а не 2. Парсер cron свой: пять полей и
// 440 итераций — не повод для зависимости; арифметика времени — только time.ts
// (правило R11), календарный шаг — целочисленный, без Date.
// ---------------------------------------------------------------------------

export interface CronSpec {
  /** null — поле «*» (не ограничено). dow: 0..6, 0 — воскресенье (7 приводится к 0). */
  readonly minutes: readonly number[];
  readonly hours: readonly number[];
  readonly dom: readonly number[] | null;
  readonly months: readonly number[] | null;
  readonly dow: readonly number[] | null;
}

/** Не менее 400 суток (ПРАВИЛО 6.21); 440 покрывает переходы DST в обе стороны. */
export const CRON_SIM_DAYS = 440;

/** Начало окна симуляции: 2026-01-01 (четверг). Фиксировано ради детерминизма проверки. */
const SIM_START = { year: 2026, month: 1, day: 1, cronDow: 4 };

function parseCronField(raw: string, min: number, max: number, label: string): number[] | null {
  if (raw === '*') return null;
  const out = new Set<number>();
  for (const token of raw.split(',')) {
    const m = /^(\*|\d{1,2}|\d{1,2}-\d{1,2})(?:\/(\d{1,2}))?$/.exec(token);
    if (m === null) throw new Error(`поле ${label}: не разобрано «${token}»`);
    const [, base, stepRaw] = m as unknown as [string, string, string | undefined];
    const step = stepRaw === undefined ? 1 : Number(stepRaw);
    if (step < 1) throw new Error(`поле ${label}: шаг 0 в «${token}»`);
    let lo: number;
    let hi: number;
    if (base === '*') {
      lo = min;
      hi = max;
    } else if (base.includes('-')) {
      const [a, b] = base.split('-').map(Number) as [number, number];
      lo = a;
      hi = b;
    } else {
      lo = Number(base);
      hi = stepRaw === undefined ? lo : max;
    }
    if (lo > hi) throw new Error(`поле ${label}: диапазон ${lo}-${hi} задом наперёд`);
    for (let v = lo; v <= hi; v += step) {
      // 7 в поле дня недели — воскресенье, как 0 (обе формы законны в cron).
      const norm = label === 'dow' && v === 7 ? 0 : v;
      if (norm < min || norm > max) {
        throw new Error(`поле ${label}: ${v} вне диапазона ${min}..${max}`);
      }
      out.add(norm);
    }
  }
  if (out.size === 0) throw new Error(`поле ${label}: пусто`);
  return [...out].sort((a, b) => a - b);
}

/** Бросает Error с текстом для человека; вызывающий превращает его в отказ конфига. */
export function parseCron(expr: string): CronSpec {
  const fields = expr.trim().split(/\s+/);
  if (fields.length !== 5) {
    throw new Error(
      `ожидалось 5 полей cron (мин час день месяц день-недели), дано ${fields.length}`,
    );
  }
  const [fMin, fHour, fDom, fMon, fDow] = fields as [string, string, string, string, string];
  return {
    minutes: parseCronField(fMin, 0, 59, 'minute') ?? range(0, 59),
    hours: parseCronField(fHour, 0, 23, 'hour') ?? range(0, 23),
    dom: parseCronField(fDom, 1, 31, 'dom'),
    months: parseCronField(fMon, 1, 12, 'month'),
    dow: parseCronField(fDow, 0, 6, 'dow'),
  };
}

function range(a: number, b: number): number[] {
  const out: number[] = [];
  for (let v = a; v <= b; v += 1) out.push(v);
  return out;
}

function isLeap(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

function daysInMonth(y: number, m: number): number {
  return [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1] as number;
}

interface Firing {
  readonly y: number;
  readonly m: number;
  readonly d: number;
  readonly hh: number;
  readonly mi: number;
  /** «Стенные» минуты от начала симуляции — линейны ВНЕ переходов DST. */
  readonly naive: number;
}

const P2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

/**
 * Момент срабатывания. Несуществующее стенное время (весенний перевод) исполняется
 * после скачка — как у cron/systemd — то есть на час позже; двусмысленное (осенний)
 * wallMinuteToMs сам берёт более ранний инстант.
 */
function instantOf(f: Firing, tz: string): number {
  const wall = `${String(f.y).padStart(4, '0')}-${P2(f.m)}-${P2(f.d)}T${P2(f.hh)}:${P2(f.mi)}`;
  try {
    return wallMinuteToMs(wall, tz).ms;
  } catch {
    let y = f.y;
    let m = f.m;
    let d = f.d;
    let hh = f.hh + 1;
    if (hh === 24) {
      hh = 0;
      d += 1;
      if (d > daysInMonth(y, m)) {
        d = 1;
        m += 1;
        if (m === 13) {
          m = 1;
          y += 1;
        }
      }
    }
    const shifted = `${String(y).padStart(4, '0')}-${P2(m)}-${P2(d)}T${P2(hh)}:${P2(f.mi)}`;
    return wallMinuteToMs(shifted, tz).ms;
  }
}

export interface ScheduleSimResult {
  readonly maxGapMs: number;
  readonly firings: number;
}

/**
 * Общий календарный прогон: обходчик суток отдаёт срабатывания дня, разрыв считается
 * по инстантам. Материализуются через time.ts не все срабатывания, а только кандидаты
 * в максимальный разрыв: DST сдвигает стенное время не больше чем на час, поэтому
 * пары с «наивным» разрывом ближе 61 минуты к максимуму достаточно.
 */
function simulateDays(
  tz: string,
  minutesOfDay: (cronDow: number, dom: number, month: number, year: number) => readonly number[],
): ScheduleSimResult {
  const firings: Firing[] = [];
  let { year: y, month: m, day: d } = SIM_START;
  let dow = SIM_START.cronDow;
  for (let day = 0; day < CRON_SIM_DAYS; day += 1) {
    for (const minute of minutesOfDay(dow, d, m, y)) {
      firings.push({
        y,
        m,
        d,
        hh: Math.floor(minute / 60),
        mi: minute % 60,
        naive: day * 1440 + minute,
      });
    }
    d += 1;
    if (d > daysInMonth(y, m)) {
      d = 1;
      m += 1;
      if (m === 13) {
        m = 1;
        y += 1;
      }
    }
    dow = (dow + 1) % 7;
  }
  if (firings.length < 2) {
    throw new Error(
      `за ${String(CRON_SIM_DAYS)} суток симуляции меньше двух срабатываний — расписание непригодно`,
    );
  }
  let maxNaive = 0;
  for (let i = 1; i < firings.length; i += 1) {
    const gap = (firings[i] as Firing).naive - (firings[i - 1] as Firing).naive;
    if (gap > maxNaive) maxNaive = gap;
  }
  let maxGapMs = 0;
  for (let i = 1; i < firings.length; i += 1) {
    const a = firings[i - 1] as Firing;
    const b = firings[i] as Firing;
    if (b.naive - a.naive < maxNaive - 61) continue;
    const gapMs = instantOf(b, tz) - instantOf(a, tz);
    if (gapMs > maxGapMs) maxGapMs = gapMs;
  }
  return { maxGapMs, firings: firings.length };
}

/** max_gap расписания мониторинга — симуляцией (ПРАВИЛО 6.21). */
export function simulateCronMaxGap(spec: CronSpec, tz: string): ScheduleSimResult {
  const minutes: number[] = [];
  for (const hh of spec.hours) for (const mi of spec.minutes) minutes.push(hh * 60 + mi);
  minutes.sort((a, b) => a - b);
  return simulateDays(tz, (cronDow, dom, month) => {
    if (spec.months !== null && !spec.months.includes(month)) return [];
    // Стандартная семантика cron: оба поля дня ограничены — сутки подходят по ЛЮБОМУ.
    const domOk = spec.dom === null ? null : spec.dom.includes(dom);
    const dowOk = spec.dow === null ? null : spec.dow.includes(cronDow);
    const dayOk =
      domOk === null && dowOk === null
        ? true
        : domOk !== null && dowOk !== null
          ? domOk || dowOk
          : (domOk ?? dowOk ?? false);
    return dayOk ? minutes : [];
  });
}

/** max_slot_wait — той же симуляцией по слотам публикации (ПРАВИЛО 6.6). */
export function simulateSlotsMaxGap(
  slots: readonly PublishingSlot[],
  tz: string,
): ScheduleSimResult {
  const byDow = new Map<number, number[]>();
  for (const s of slots) {
    const cronDow = s.weekday % 7; // ISO 1..7 → cron 0..6 (воскресенье 7 → 0)
    const [hh, mi] = s.time.split(':').map(Number) as [number, number];
    const list = byDow.get(cronDow) ?? [];
    list.push(hh * 60 + mi);
    byDow.set(cronDow, list);
  }
  for (const list of byDow.values()) list.sort((a, b) => a - b);
  return simulateDays(tz, (cronDow) => byDow.get(cronDow) ?? []);
}

const HOUR_MS = 3_600_000;

/**
 * Хост списка разрешённых адресов: голое имя, без схемы, без пути, без порта, в нижнем
 * регистре. Регистр проверяется, а не приводится: `isHostAllowed` сравнивает строки, и
 * молчаливое приведение здесь означало бы, что запись `GeoBoost.RU` в файле и запись
 * `geoboost.ru` — одно и то же для схемы, но разное для грепа по конфигам.
 *
 * Метка допускает буквы и цифры любого письма (`почта.рф` — законный хост), но не может
 * начинаться или кончаться дефисом. Точка обязательна: односоставное имя (`localhost`) —
 * это не домен бренда.
 */
const HOST_LABEL = '[\\p{L}\\p{N}](?:[\\p{L}\\p{N}-]*[\\p{L}\\p{N}])?';
const ALLOWED_HOST_RE = new RegExp(`^${HOST_LABEL}(?:\\.${HOST_LABEL})+$`, 'u');

export const AllowedHost = z
  .string()
  .regex(ALLOWED_HOST_RE, 'ожидался хост вида geoboost.ru: без схемы, без пути, без порта')
  .refine((h) => h === h.toLowerCase(), 'хост записывается строчными буквами');

/**
 * Белый список адресов, которые вправе появиться в посте бренда. Решение Ильи 25.08
 * (`docs/PHASE-2.md` §2.1), ПРАВИЛО C3-1 `docs/phase-2/06-security-ops.md`.
 *
 * Здесь лежит ТОЛЬКО статическая половина списка — домены самого бренда. Вторая половина,
 * домен первоисточника темы, статикой быть не может: она известна только когда у черновика
 * есть тема. Сводит их валидатор версии на приёме; сама проверка — `checkLinkPolicy`
 * в `telegram/draft.ts`, там же расписано, почему половин две.
 *
 * Секция необязательна по той же причине, что и `freshness`: `strictObject` роняет старт на
 * любом бренде без неё. Отсутствие означает пустой список, то есть «внешних ссылок нет» —
 * и это безопасный дефолт, в отличие от `freshness`, где отсутствие выключает проверку.
 */
export const LinksSchema = z.strictObject({
  allowed_hosts: z.array(AllowedHost),
});

/**
 * Модели, которым известна цена. Перечисление — копия ключей `models` из
 * `config/llm-prices.yaml`, и копия сознательная: прайс-лист читает ТОЛЬКО бэкенд
 * (03-generation.md §12.3, Р8), а конфиг бренда грузится в сервисе, у которого этого файла
 * нет и быть не должно. Модель без цены даёт `422 unknown_model_price` на маршруте учёта —
 * то есть отказ приходит ПОСЛЕ того, как за вызов заплачено; перечисление здесь ловит ту же
 * ошибку при загрузке конфига. Сверку списка с файлом держит тест: разойтись молча они не
 * могут.
 */
export const GENERATION_MODELS = [
  'claude-opus-5-5',
  'claude-opus-5',
  'claude-opus-4-8',
  'claude-opus-4-7',
  'claude-sonnet-5',
  'claude-haiku-4-5',
] as const;

/**
 * Единственная модель конвейера мониторинга (ПРАВИЛО 3.1). Живёт в контрактах, а не только в
 * `services/agent-monitor`, по одной причине: бэкенд на старте сверяет, что у каждой модели,
 * которой платят сервисы, есть цена в `llm-prices.yaml` (P2, ревизия 2026-10-18, находка 6), а
 * импортировать сервис мониторинга бэкенд не вправе (R3). Мониторинг берёт её отсюда же.
 */
export const MONITOR_MODEL = 'claude-sonnet-5';

/**
 * Уровни `output_config.effort` — перечисление `OutputConfig` установленного
 * `@anthropic-ai/sdk@0.120.0` (03-generation.md §6.3, §17). Значение конфига относится к
 * вызовам «напиши версию»: у короткого вызова «выбери формат» усилие своё, ниже, и оно
 * решение кода, а не бренда.
 */
export const GENERATION_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;

/**
 * Секция генерации бренда — 03-generation.md §3.5 и §5.2, зависимость З3.
 *
 * Одна модель на всю серию вызовов поста: смена модели посреди серии — это второй префикс и
 * вторая запись кэша (§6.3). `versions_per_post` — сколько УГЛОВ пишется подряд (Р3):
 * версия N обязана видеть версию N−1, поэтому вызовы последовательны по построению, а не по
 * решению; в intake едет одна победившая версия (§6.5).
 */
export const GenerationSchema = z.strictObject({
  model: z.enum(GENERATION_MODELS),
  effort: z.enum(GENERATION_EFFORTS),
  versions_per_post: z.int().min(1).max(3),
  /**
   * НОРМЫ ЗНАКОВ — потолки на пост, а не цели (решение 2026-08-30, вариант 2).
   *
   * До этого решения здесь стоял `style_avoid`: список жестов, которые бросок формы обнулял,
   * а всё остальное он ЗАКАЗЫВАЛ числом («двоеточия: 2», «длинное тире: 4»). Замер прогонов
   * показал, чем это кончается: двоеточия заказывались в 78% постов, тире в 60%, восклицания
   * в 55%, и модель ставила их в самую дешёвую позицию — пояснительное двоеточие,
   * тире-приложение, восклицание в конце ударной строки. Квота говорит СКОЛЬКО и никогда не
   * говорит ГДЕ, поэтому она и не может дать ничего, кроме машинной расстановки.
   *
   * Теперь бросок знаков не касается вовсе, а пунктуацию держат нормы бренда, и приходят они
   * не из жребия, а из документов владельца голоса: `docs/brand/1.6_publication_rules.md` §3
   * и `docs/brand/05_tone_of_voice.md` §6.1.
   *
   * Ключ не задан — правило МОЛЧИТ целиком, а не считается нулём: умолчание в коде завело бы
   * второй источник норм рядом с этим файлом, и разошлись бы они молча. Это свойство БРЕНДА,
   * а не процесса: демон работает по всем брендам сразу, и общий на всех потолок означал бы
   * голос geoboost в канале StrategAI.
   */
  style_limits: z
    .strictObject({
      /** Настоящее тире и дефис в его роли. 1.6 §3 п.9: «не больше одного-двух на весь текст». */
      dash: z.int().nonnegative().optional(),
      /** Конструкция «утверждение: пояснение». Замечание PR lead №1; перечисления не в счёт. */
      colon_explainer: z.int().nonnegative().optional(),
      /** Замечание PR lead №4. В брендовых документах нормы нет — ждёт подтверждения. */
      exclaim: z.int().nonnegative().optional(),
      /** 1.6 §7 и 05 §6.1 п.9: эмодзи-разметка запрещена, эмодзи в тексте «редко и по смыслу». */
      emoji: z.int().nonnegative().optional(),
      /** «Не X, а Y». 1.6 §3 п.7 и 05 §6.1 п.1: «не больше одной, лучше ноль». */
      antithesis: z.int().nonnegative().optional(),
    })
    .optional(),
  /**
   * ПОТОЛОК ВЫДЕРЖКИ СТАТЬИ В БРИФЕ ПИСАТЕЛЯ, кодпоинты (решение 2026-09-21).
   *
   * Мониторинг хранит выдержку до `monitoring.evidence_excerpt_max` (6000), и с релиза 0919
   * она впервые доезжает до писателя (§8: до него терялась, и писатель видел заголовок и
   * лид). С того же вечера вызов «напиши версию» на Opus 5 стал обрываться отказом
   * классификатора `reasoning_extraction` — 14 из 19 попыток по пяти темам, у каждой
   * выдержка 3,6–6 тыс. знаков; до релиза отказов не было ни одного. Потолок режет выдержку
   * ТОЛЬКО на входе в бриф: в базе она остаётся целиком, и поднять его обратно — правка
   * конфига, а не повторный обход.
   *
   * Ключ не задан — выдержка идёт целиком, как в релизе 0919.
   */
  material_excerpt_max: z.int().min(200).max(6000).optional(),
  /**
   * КРАСНЫЕ ПРАВИЛА ГЕЙТА «ЖИВОГО ТЕКСТА», СНЯТЫЕ ДЛЯ БРЕНДА (docs/multi-brand.md §5).
   *
   * Часть правил `@smm/style` написана под голос geoboost и включена всегда: «для маркетолога
   * это меняет» (`changes_for_reader`), совет читателю «проверьте сами» (`reader_diy`),
   * формула в закрытии (`aphorism_close`). У бренда с другим голосом такое правило красит
   * законный жест канала и заставляет переписывающий проход его вычищать. Здесь — коды,
   * которые гейт для этого бренда не считает красными; к снятиям формата они добавляются.
   *
   * Форма кода проверяется схемой, а принадлежность к красным кодам пакета — писателем при
   * старте: `@smm/contracts` не знает списка правил `@smm/style` и знать не должен.
   * Ключ не задан — снятий нет, гейт тот же, что был.
   */
  style_exempt: z
    .array(z.string().regex(/^[a-z][a-z_]{1,47}$/, 'ожидался код правила гейта вида reader_diy'))
    .max(32)
    .optional(),
  /**
   * «ДОБАВИТЬ МНЕНИЕ» — мнение автора канала внутри поста (AGI Boardroom, решение Ильи
   * 2026-11-02; контракт — `api/author-opinion.ts`). Блок есть — функция включена: интерфейс
   * показывает полосу мнения, бэкенд принимает маршрут, писатель знает режим. Нет блока —
   * маршрут отвечает 409, и бренд живёт как жил.
   *
   * Живёт в секции генерации, потому что без писателя мнения не вставить: бренд без
   * `generation` его иметь не может по построению.
   *
   * `max_codepoints` — потолок сырого текста в диалоге. Верх — `AUTHOR_OPINION_MAX_CODEPOINTS`:
   * длиннее база не примет (CHECK 0028), и конфиг, разрешающий больше, обещал бы человеку то,
   * что сохранить нельзя, — отказ пришёл бы не при загрузке файла, а при первом мнении.
   * Читать — через `authorOpinionOf`.
   */
  author_opinion: z
    .strictObject({
      max_codepoints: z.int().min(100).max(AUTHOR_OPINION_MAX_CODEPOINTS),
    })
    .optional(),
});

/**
 * АВТОМАТИКА ГЕНЕРАЦИИ ПО РАСПИСАНИЮ (релиз 2026-10-14, docs/release-2026-10-14-news-digest.md).
 *
 * Два вида, и у каждого своё расписание: `news` — одна новость (пн/ср/пт 09:00 у geoboost),
 * `digest` — «Дайджест недели» (пт 10:00). Исполнитель — бэкенд, а не таймер мониторинга:
 * тик обслуживания в назначенную минуту зоны `publishing.timezone` кладёт в очередь ОДНУ
 * заявку на мониторинг вида `topic` без заявки генерации и без автора
 * (`services/backend/src/jobs/auto-generation.ts`), ручной прогон заводит по ней одну тему,
 * а сметающий проход писателя пишет её в черновик «ожидает». Публикует по-прежнему только
 * человек (FR-4.1). `monitoring.schedule` и ПРАВИЛО 6.6 этим блоком не затрагиваются.
 *
 * Блок необязателен по той же причине, что `freshness` и `visual`: `strictObject` роняет
 * старт на любом бренде без него. Нет блока (или вида в нём) — нет автоматики.
 *
 * `until` — край ДОГОНА в тот же день: бэкенд, лежавший в назначенную минуту, кладёт
 * заявку позже, но не позже `until` и никогда на другой день. Не задан — умолчание вида
 * (`AUTO_GENERATION_DEFAULT_UNTIL`): новость до 18:00, дайджест до 15:00 (он уходит в слот
 * 17:00, и согласующему нужно время). Повтор в те же сутки не кладётся никогда.
 */
export const AUTO_GENERATION_KINDS = ['news', 'digest'] as const;
export type AutoGenerationKind = (typeof AUTO_GENERATION_KINDS)[number];

/** `format_hint` заявки на мониторинг по виду автоматики — коды файлов формата. */
export const AUTO_GENERATION_FORMAT: Readonly<Record<AutoGenerationKind, string>> = {
  news: 'news',
  digest: 'weekly-digest',
};

/** Край догона по умолчанию, «стенное» время зоны бренда. */
export const AUTO_GENERATION_DEFAULT_UNTIL: Readonly<Record<AutoGenerationKind, string>> = {
  news: '18:00',
  digest: '15:00',
};

export const AutoGenerationJobSchema = z.strictObject({
  /** ISO-8601, как у слота: 1 — понедельник, 7 — воскресенье. */
  days: z.array(z.int().min(1).max(7)).min(1).max(7),
  /** Назначенная минута, `HH:MM` зоны `publishing.timezone`. */
  time: z.string().regex(SLOT_TIME_RE, 'ожидалось HH:MM (00:00–23:59)'),
  /** Край догона в тот же день, `HH:MM`; позже `time`. */
  until: z.string().regex(SLOT_TIME_RE, 'ожидалось HH:MM (00:00–23:59)').optional(),
});
export type AutoGenerationJob = z.output<typeof AutoGenerationJobSchema>;

export const AutoGenerationSchema = z.strictObject({
  news: AutoGenerationJobSchema.optional(),
  digest: AutoGenerationJobSchema.optional(),
});
export type AutoGeneration = z.output<typeof AutoGenerationSchema>;

/** Действующий край догона вида: свой `until` или умолчание вида. */
export function autoGenerationUntil(kind: AutoGenerationKind, job: AutoGenerationJob): string {
  return job.until ?? AUTO_GENERATION_DEFAULT_UNTIL[kind];
}

export const PublishingSchema = z.strictObject({
  /** Обязателен: отсюда берут зону материализация слота, `day_key` публикатора и показ времени в UI. */
  timezone: IanaTimeZone,
  slots: z.array(PublishingSlotSchema),
  max_per_day: z.int().positive(),
});

/**
 * Палитра карточки и подпись-домен. §3: «Палитра — из конфига бренда, не из литерала…
 * шаблон обязан уметь спросить палитру, а не знать её.»
 *
 * Секция необязательна ровно по той же причине, что `knowledge` и `generation`: конфиги,
 * которые уже лежат на дроплете, её не имеют, и требовать её значило бы, что бэкенд не
 * стартует до правки файла руками. Отсутствие — законное состояние «бренду картинок не
 * рисуют»; сервис генерации спрашивает секцию сам и отказывается рисовать без неё, называя
 * файл — тот же приём, что у пустого channel_id.
 *
 * Значения — HEX, ровно шесть цифр. Ни имён токенов, ни `rgb()`, ни alpha: шаблон
 * подставляет строку в SVG-атрибут, и всё, что не является цветом по SVG 1.1, там
 * молча превращается в чёрный.
 */
const HEX_COLOR = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'ожидался цвет вида #rrggbb');

export const VisualBrandSchema = z.strictObject({
  palette: z.strictObject({
    /** Фон полотна. */
    bg: HEX_COLOR,
    /** Основной текст. */
    ink: HEX_COLOR,
    /** Второстепенный текст. */
    ink_2: HEX_COLOR,
    /** Подписи и источник. */
    muted: HEX_COLOR,
    /** Линии и рамки. §3: тоньше 2 px не рисуем — Telegram пережимает в JPEG. */
    border: HEX_COLOR,
    /** Акцент: метка, цифра, знак бренда. */
    accent: HEX_COLOR,
    /** Подложка акцента. */
    accent_bg: HEX_COLOR,
  }),
  /**
   * Подпись карточки: домен или адрес канала без протокола (`geoboost.ru`,
   * `t.me/agi_boardroom`). Не URL: на карточке нет ни ссылок, ни протокола. Косая черта и
   * подчёркивание разрешены с 2026-10-14 ради адреса канала у бренда без сайта.
   */
  wordmark_domain: z
    .string()
    .regex(/^[a-z0-9.-]+(?:\/[a-z0-9_.-]+)?$/, 'ожидался домен или адрес канала без протокола')
    .min(3)
    .max(64),
  /**
   * Знак бренда на карточке. `dots` — сетка точек GeoBoost (умолчание, так рисовалось всегда);
   * `none` — знака нет, карточку подписывает только `wordmark_domain`. Знак — чужой логотип
   * для любого бренда, кроме того, чей он есть, поэтому второй бренд обязан выбрать явно.
   */
  mark: z.enum(['dots', 'none']).optional(),
  /**
   * Обложка поста (docs/phase-2/10-posters.md): фон из набора, дата слота, метка канала и
   * крючок. Есть секция — обложка рисуется к КАЖДОМУ новому посту бренда и едет внутри
   * версии (решение Ильи 2026-10-28); нет — бренд живёт как жил, без картинки по умолчанию.
   *
   * Необязательна по той же причине, что и вся `visual`: конфиги на дроплете её не имеют, и
   * требовать её значило бы, что сервисы не стартуют до правки файла руками.
   */
  poster: PosterBrandSchema.optional(),
});
export type VisualBrand = z.output<typeof VisualBrandSchema>;

/**
 * ВНЕШНИЙ ВИД БРЕНДА В ИНТЕРФЕЙСЕ СОГЛАСОВАНИЯ (docs/multi-brand.md §6).
 *
 * Тема — имя набора токенов, который несёт `packages/ui` (`styles/tokens.css`): конфиг
 * ВЫБИРАЕТ тему, но не описывает её цветами — цвета интерфейса живут в одном месте, и
 * второй бренд с той же темой не копирует палитру. Новая тема — правка UI, новый бренд с
 * существующей темой — только этот файл.
 *
 * Блок необязателен: конфиги на дроплете его не имеют. Умолчания — тема `geoboost`, порядок
 * `UI_DEFAULT_ORDER`, вордмарк — `display_name`; их подставляет бэкенд (`GET /api/v1/brands`).
 *
 * Темы: `geoboost` — светлая, фиолетовый акцент; `strategai` — тёмная, оранжевый акцент
 * (AGI Boardroom); `strategai-light` — светлая StrategAI: белый основной, оранжевый #F2731F
 * второй (AI Lab, решение Ильи 2026-10-25, docs/ai-lab.md). Имя темы с дефисом законно —
 * оно же атрибут `data-theme`, а не ключ CSS-модуля.
 */
export const BRAND_THEMES = ['geoboost', 'strategai', 'strategai-light'] as const;
export type BrandTheme = (typeof BRAND_THEMES)[number];

/** Порядок бренда без `ui.order`: список брендов сортируется по (order, id). */
export const UI_DEFAULT_ORDER = 100;

export const BrandUiSchema = z.strictObject({
  theme: z.enum(BRAND_THEMES),
  /** Место в переключателе брендов и выбор корня `/`: меньше — раньше. */
  order: z.int().min(0).max(999).optional(),
  /** Текст знака в шапке (у тем `strategai*` — после косой черты). Одна строка. */
  wordmark: z
    .string()
    .regex(/^[^\r\n<>]{1,32}$/u, 'одна строка до 32 символов без угловых скобок')
    .optional(),
});
export type BrandUi = z.output<typeof BrandUiSchema>;

/**
 * АВТООДОБРЕНИЕ — ЗАМОК 1 ИЗ 3 обдуманного исключения из FR-4.1 (решение Ильи 2026-10-25,
 * docs/ai-lab.md). Бренд, у которого здесь `allowed: true`, МОЖЕТ получить в интерфейсе
 * выключатель «Автоодобрение» (только админ, по умолчанию выключен); включённый, он одобряет
 * каждую новую версию писателя в ближайший свободный слот без человека.
 *
 * Один этот блок не одобряет ничего. Пройти обязаны все три замка, каждый держится сам:
 *   1. бэкенд — этот блок, строка выключателя `auto_approval:<бренд>` во включённом
 *      положении и триггер базы, пропускающий не-человеческий переход
 *      pending_review → scheduled только при включённой строке ЭТОГО бренда;
 *   2. демон подписи — свой root-owned allowlist `auto-approval.json`;
 *   3. публикатор — свой root-owned allowlist того же вида.
 *
 * Отсутствие блока ≡ `allowed: false`: на дроплете конфиги без него, и исключение не может
 * появиться у бренда молча. Читать — только через `autoApprovalAllowed`.
 */
export const AutoApprovalConfigSchema = z.strictObject({
  allowed: z.boolean(),
});
export type AutoApprovalConfig = z.output<typeof AutoApprovalConfigSchema>;

/**
 * ЮРИДИЧЕСКИЕ ПОМЕТКИ ПОСТА (AGI Boardroom, решение Ильи 2026-11-02 — «очень важно»). Сегодня
 * одна: `banned_orgs` — звёздочка после названия организации, запрещённой в РФ, и строка
 * «* Запрещена в РФ» последним блоком (`applyBannedOrgMarks`, `@smm/contracts/preview`).
 *
 * Включает разметку писателю (сборка черновика) и бэкенду (заморозка любой версии бренда:
 * писатель, правка человеком, картинка, документ) и страховку одобрения. Обе стороны читают
 * ключ при старте из одного файла, и выкатывается он только вместе с кодом, который его знает.
 * Список названий — код, а не конфиг: здесь только выключатель.
 *
 * Блок необязателен; отсутствие ≡ `false`, и бренд без блока не меняется ни на байт. Читать —
 * только через `bannedOrgMarksOn`.
 */
export const LegalSchema = z.strictObject({
  banned_orgs: z.boolean(),
});
export type BrandLegal = z.output<typeof LegalSchema>;

/**
 * Код выпуска конференции в списке наблюдения: строчная латиница, цифры, дефис; уходит в
 * отпечаток дедупа темы `event-review:<код>:<год>` и в тело темы (`IntakeTopicBodySchema.event`).
 * Один код — один ВЫПУСК, а не серия: у KubeCon два выпуска в год (`kubecon-eu`, `kubecon-na`),
 * у Apple — WWDC и осенняя презентация, и общий код запретил бы второй обзор года.
 */
export const EVENT_ID_RE = /^[a-z][a-z0-9-]{1,39}$/;

/**
 * Потолок материалов обзора — тот же, что у любой темы (`TOPIC_MATERIALS_MAX`, `api/intake.ts`):
 * больше тема не примет. Копия числа, а не импорт: приём темы читает отсюда `EVENT_ID_RE`, и
 * импорт в обратную сторону замкнул бы цикл модулей. Равенство держит тест.
 */
export const EVENT_WATCH_MATERIALS_MAX = 10;

const MONTHS_IN_YEAR = 12;

/** Строка конфига в одну строку без краевых пробелов: имя и ключевые слова уходят в промпт и заголовок. */
const OneLine = (min: number, max: number) =>
  z
    .string()
    .min(min)
    .max(max)
    .regex(/^[^\r\n]+$/u, 'одна строка')
    .refine((s) => s.trim() === s, 'без краевых пробелов');

/**
 * Один выпуск конференции, за которым следит бренд.
 *   · `name` — как его назвать человеку и в заголовке обзора («Обзор: <name> <год>»);
 *   · `keywords` — слова, по которым мониторинг узнаёт выпуск в заголовке и лиде (NFKC, без
 *     учёта регистра, целым словом); короткое общее слово ловит шум, и его отсекает только
 *     подтверждение моделью;
 *   · `sources` — коды реестра, где выходят собственные анонсы организатора; существование и
 *     включённость кода проверяет мониторинг при старте (предупреждением) и тест репозитория;
 *   · `months` — когда выпуск бывает (1–12, в зоне `publishing.timezone`): вне сезона прогон по
 *     расписанию его не ищет вовсе, кнопка — ищет всегда.
 */
export const EventWatchEventSchema = z
  .strictObject({
    id: z
      .string()
      .regex(EVENT_ID_RE, 'ожидался код выпуска вида kubecon-eu: ^[a-z][a-z0-9-]{1,39}$'),
    name: OneLine(2, 80),
    keywords: z.array(OneLine(2, 64)).min(1).max(20),
    sources: z
      .array(z.string().regex(SOURCE_CODE_RE, 'ожидался код источника реестра'))
      .min(1)
      .max(20),
    months: z.array(z.int().min(1).max(MONTHS_IN_YEAR)).min(1).max(MONTHS_IN_YEAR),
  })
  .check((ctx) => {
    // Повтор внутри списка — опечатка: месяц дважды ничего не добавляет, ключевое слово дважды
    // (с точностью до регистра и NFKC — так их и сравнивает мониторинг) — тоже.
    const dupes = (path: 'keywords' | 'sources' | 'months', keys: readonly string[]): void => {
      const seen = new Map<string, number>();
      keys.forEach((k, i) => {
        const first = seen.get(k);
        if (first === undefined) {
          seen.set(k, i);
          return;
        }
        ctx.issues.push({
          code: 'custom',
          input: ctx.value,
          path: [path, i],
          message: `повторяет ${path}[${String(first)}]`,
        });
      });
    };
    dupes(
      'keywords',
      ctx.value.keywords.map((k) => k.normalize('NFKC').toLowerCase()),
    );
    dupes('sources', ctx.value.sources);
    dupes(
      'months',
      ctx.value.months.map((m) => String(m)),
    );
  });
export type EventWatchEvent = z.output<typeof EventWatchEventSchema>;

/**
 * СПИСОК НАБЛЮДЕНИЯ ЗА КОНФЕРЕНЦИЯМИ — обзор конференции, формат `event-review` (AGI Boardroom,
 * решение Ильи 2026-11-02). Каждый день прогон новости по расписанию проверяет, не прошёл ли
 * выпуск из списка в своём сезоне; прошёл — заводит ОДНУ тему обзора на выпуск в год; нет —
 * молчит, без вызова модели. Кнопка «Обзор конференции» ищет по всему списку вне сезона.
 *
 * Живёт в конфиге бренда, а не в реестре и не в базе знаний: этот файл — единственный, который
 * читают все трое — бэкенд (выпуск темы обязан быть в списке), мониторинг и писатель.
 *
 * Блок необязателен; нет блока — обзоров нет. Внутри блока умолчания законны (`lookback_hours`
 * 48, `max_materials` 8): блок новый, и прежних конфигов они не меняют.
 */
export const EventWatchSchema = z
  .strictObject({
    /** Окно поиска анонсов прогоном по расписанию, часы: выпуск вчера или сегодня. */
    lookback_hours: z.int().min(24).max(168).default(48),
    /** Сколько материалов кладётся в тему обзора (ведущий + поддерживающие). */
    max_materials: z.int().min(1).max(EVENT_WATCH_MATERIALS_MAX).default(8),
    events: z.array(EventWatchEventSchema).min(1).max(100),
  })
  .check((ctx) => {
    // Код выпуска — ключ отпечатка дедупа: два выпуска под одним кодом делили бы один обзор в год.
    const seen = new Map<string, number>();
    ctx.value.events.forEach((e, i) => {
      const first = seen.get(e.id);
      if (first === undefined) {
        seen.set(e.id, i);
        return;
      }
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['events', i, 'id'],
        message: `код выпуска «${e.id}» повторяет events[${String(first)}]`,
      });
    });
  });
export type EventWatch = z.output<typeof EventWatchSchema>;

export const BrandConfigSchema = z
  .strictObject({
    id: z.string().regex(BRAND_RE, 'ожидался код бренда вида ^[a-z][a-z0-9_-]{1,31}$'),
    display_name: z.string().min(1).max(64),
    channels: z.strictObject({
      telegram: z.strictObject({
        /** Боевой канал: режим публикаций `live` в профиле prod. */
        channel_id: TelegramChatId,
        /** Закрытый тестовый канал: режим `test` в prod и оба режима в dev. */
        test_channel_id: TelegramChatId,
        /** Привязка боевого канала к Telegram — см. `ChannelRefSchema`. Только при непустом id. */
        channel_ref: ChannelRefSchema.optional(),
        /** Привязка тестового канала — то же правило. */
        test_channel_ref: ChannelRefSchema.optional(),
      }),
    }),
    publishing: PublishingSchema,
    freshness: FreshnessSchema.optional(),
    links: LinksSchema.optional(),
    monitoring: MonitoringSchema.optional(),
    /**
     * База знаний Б1 (docs/phase-2/02-knowledge.md §5.5). Необязательна СОЗНАТЕЛЬНО:
     * конфиги фазы 1 раздела не имеют, и на них стоит весь контур вместе со шлюзом.
     * Отсутствие — законное состояние «бренд без базы знаний»; сервисы генерации и
     * мониторинга требуют своего непустого списка сами при старте (`loadKnowledge`)
     * и падают с текстом, называющим файл, — тот же приём, что у пустого channel_id.
     */
    knowledge: KnowledgeRefsSchema.optional(),
    /**
     * Модель, усилие и число версий сервиса генерации (§3.5, §5.2). Необязательна по той же
     * причине, что и `knowledge`: конфиги фазы 1 секции не имеют, и на них стоит весь контур.
     * Отсутствие — законное состояние «бренд не генерируется»; сервис генерации требует
     * секцию сам при старте и отказывается работать по бренду без неё, называя файл.
     */
    generation: GenerationSchema.optional(),
    visual: VisualBrandSchema.optional(),
    /** Генерация по расписанию — см. `AutoGenerationSchema`. */
    auto_generation: AutoGenerationSchema.optional(),
    /** Внешний вид в интерфейсе согласования — см. `BrandUiSchema`. */
    ui: BrandUiSchema.optional(),
    /** Автоодобрение, замок 1 из 3 — см. `AutoApprovalConfigSchema`. */
    auto_approval: AutoApprovalConfigSchema.optional(),
    /** Юридические пометки поста — см. `LegalSchema`. */
    legal: LegalSchema.optional(),
    /** Наблюдение за конференциями, обзоры `event-review` — см. `EventWatchSchema`. */
    event_watch: EventWatchSchema.optional(),
  })
  .check((ctx) => {
    // Повтор слота развалился бы не здесь, а в материализаторе: UNIQUE (brand_code, slot_at_ms)
    // отбивает вторую строку, и расписание бренда молча теряло бы по слоту в неделю.
    const seen = new Map<string, number>();
    ctx.value.publishing.slots.forEach((s, i) => {
      const key = `${s.weekday}T${s.time}`;
      const first = seen.get(key);
      if (first === undefined) {
        seen.set(key, i);
        return;
      }
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['publishing', 'slots', i],
        message: `слот ${key} повторяет slots[${first}]`,
      });
    });

    // Режим публикаций выбирает между двумя каналами (api/operations.ts). Один и тот же id
    // в обоих полях превращает тестовый режим в публикацию в боевой канал — без единого
    // предупреждения и с подписью, которую публикатор честно примет. Поэтому это отказ
    // загрузки, а не замечание: разделение режимов держится на том, что каналов ДВА.
    const { channel_id: live, test_channel_id: test } = ctx.value.channels.telegram;
    if (live !== '' && live === test) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['channels', 'telegram', 'test_channel_id'],
        message:
          `совпадает с channel_id (${live}): боевой и тестовый каналы обязаны быть разными — ` +
          'иначе тестовый режим публикует в боевой канал',
      });
    }

    // Привязка без id не проверяет ничего: предстарт сверяет КАНАЛ, а канала нет. Молча
    // принятая, она выглядела бы защитой, которой нет, — до того дня, когда id впишут руками
    // на сервере, а привязку оставят от другого канала.
    const tg = ctx.value.channels.telegram;
    for (const [ref, id, field, refField] of [
      [tg.channel_ref, live, 'channel_id', 'channel_ref'],
      [tg.test_channel_ref, test, 'test_channel_id', 'test_channel_ref'],
    ] as const) {
      if (ref === undefined || id !== '') continue;
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['channels', 'telegram', refField],
        message: `привязка ${ref} при пустом ${field}: впишите id канала или уберите привязку`,
      });
    }

    // Автоматика по расписанию: повтор дня — опечатка, а край догона раньше назначенной
    // минуты — расписание, которое не сработает НИКОГДА, и узнали бы об этом по тишине.
    // Сравнение строк `HH:MM` законно: формат с ведущими нулями упорядочен лексикографически.
    const auto = ctx.value.auto_generation;
    if (auto !== undefined) {
      for (const kind of AUTO_GENERATION_KINDS) {
        const job = auto[kind];
        if (job === undefined) continue;
        const days = new Set<number>();
        job.days.forEach((d, i) => {
          if (days.has(d)) {
            ctx.issues.push({
              code: 'custom',
              input: ctx.value,
              path: ['auto_generation', kind, 'days', i],
              message: `день ${d} повторяется`,
            });
          }
          days.add(d);
        });
        const until = autoGenerationUntil(kind, job);
        if (until <= job.time) {
          ctx.issues.push({
            code: 'custom',
            input: ctx.value,
            path: ['auto_generation', kind, job.until === undefined ? 'time' : 'until'],
            message:
              `край догона ${until} не позже назначенного времени ${job.time}: заявка не ` +
              'положится никогда — задайте until позже time',
          });
        }
      }
    }

    // Список наблюдения за конференциями без секции мониторинга или без генерации — функция,
    // которая не сработает НИКОГДА: выпуск ищет мониторинг, обзор пишет писатель, и узнали бы об
    // этом по тишине, которая у обзоров — законное состояние «конференций не было».
    //
    // Нужна СЕКЦИЯ, а не `enabled: true`: выключить мониторинг бренда — аварийная ручка в одну
    // строку, и она не обязана требовать второй правки. Иначе файл с `enabled: false` и списком
    // наблюдения не загрузился бы, а с ним — каталог брендов у всех служб сразу.
    if (ctx.value.event_watch !== undefined) {
      if (ctx.value.monitoring === undefined) {
        ctx.issues.push({
          code: 'custom',
          input: ctx.value,
          path: ['event_watch'],
          message:
            'event_watch без секции monitoring — выпуски конференций некому искать ' +
            '(обзоры ищет прогон мониторинга бренда)',
        });
      }
      if (ctx.value.generation === undefined) {
        ctx.issues.push({
          code: 'custom',
          input: ctx.value,
          path: ['event_watch'],
          message: 'event_watch без секции generation — обзор некому написать',
        });
      }
    }

    // ── Проверки §13 01-monitoring.md: несогласованная комбинация «расписание +
    // SLA + шаг слотов» НЕДЕПЛОИМА, а не обнаруживается в проде. Живут в схеме,
    // а не в загрузчике: любой потребитель (бэкенд, мониторинг, генератор таймера)
    // получает отказ одним и тем же путём — ConfigError → выход 2.
    const mon = ctx.value.monitoring;
    if (mon === undefined) return;
    const tz = ctx.value.publishing.timezone;

    let maxGapMs: number | null = null;
    try {
      maxGapMs = simulateCronMaxGap(parseCron(mon.schedule), tz).maxGapMs;
    } catch (e) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['monitoring', 'schedule'],
        message: `расписание «${mon.schedule}»: ${e instanceof Error ? e.message : String(e)}`,
      });
    }

    let maxSlotWaitMs: number | null = null;
    if (ctx.value.publishing.slots.length === 0) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['publishing', 'slots'],
        message: 'мониторинг требует хотя бы один слот публикации: max_slot_wait не посчитать',
      });
    } else {
      try {
        maxSlotWaitMs = simulateSlotsMaxGap(ctx.value.publishing.slots, tz).maxGapMs;
      } catch (e) {
        ctx.issues.push({
          code: 'custom',
          input: ctx.value,
          path: ['publishing', 'slots'],
          message: e instanceof Error ? e.message : String(e),
        });
      }
    }

    if (ctx.value.freshness === undefined) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['freshness'],
        message:
          'у бренда с мониторингом секция freshness обязательна: без max_age_at_publish_hours ' +
          'сквозной бюджет задержки (ПРАВИЛО 6.6) нечем проверить, а окно свежести у публикатора выключено',
      });
    }

    if (maxGapMs === null || maxSlotWaitMs === null || ctx.value.freshness === undefined) return;

    // ПРАВИЛО 6.6 (max_gap + approval_sla + max_slot_wait ≤ max_age_at_publish) с 19.10.2026
    // НЕ отказ загрузки, а предупреждение старта бэкенда (`latencyBudgetProblem`, P2 ревизии
    // 2026-10-18, находка 58). Возраст материала при публикации с 19.09.2026 не проверяет никто
    // (ни кнопка, ни подпись, ни публикатор), а `monitoring.schedule` не исполняется (таймер
    // выключен, автоматика его не читает) — то есть правило охраняло гарантию, которой нет, и
    // при этом валило старт ВСЕХ сервисов ВСЕХ брендов одной правкой слотов.

    // Проверка полураспада — рекомендованная форма вопроса №3 (01-monitoring §10.3, §18):
    // half_life_abs = half_life_ratio × max_gap ≥ max_gap / 2, то есть ratio ≥ 0.5.
    // РЕШЕНИЕ ИЛЬИ ОЖИДАЕТСЯ: буква ПРАВИЛА 6.5 даёт ratio ≥ 1 и убивает различающую
    // способность кривой; дефолт §13 (0.35) эту форму НЕ проходит — бренд с мониторингом
    // задаёт half_life_ratio явно, пока вопрос №3 не закрыт.
    const halfLifeAbsMs = mon.half_life_ratio * maxGapMs;
    if (halfLifeAbsMs < maxGapMs / 2) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['monitoring', 'half_life_ratio'],
        message:
          `half_life_abs = ${(halfLifeAbsMs / HOUR_MS).toFixed(1)} ч < max_gap/2 = ` +
          `${(maxGapMs / 2 / HOUR_MS).toFixed(1)} ч (форма вопроса №3, 01-monitoring §10.3): ` +
          'поднимите half_life_ratio до ≥ 0.5 или дождитесь решения Ильи по №3',
      });
    }
  });

export type BrandConfig = z.output<typeof BrandConfigSchema>;
export type PublishingSlot = z.output<typeof PublishingSlotSchema>;
export type BrandMonitoring = z.output<typeof MonitoringSchema>;
export type BrandFreshness = z.output<typeof FreshnessSchema>;
export type BrandGeneration = z.output<typeof GenerationSchema>;
export type GenerationModel = (typeof GENERATION_MODELS)[number];
export type GenerationEffort = (typeof GENERATION_EFFORTS)[number];

/**
 * Разрешает ли КОНФИГ бренду автоодобрение (замок 1 из 3, `AutoApprovalConfigSchema`). Одно
 * правило на бэкенд, инструменты и проверки выкладки: «нет блока» и `allowed: false` — одно и
 * то же «нельзя», и третьего толкования быть не должно.
 */
export function autoApprovalAllowed(cfg: Pick<BrandConfig, 'auto_approval'>): boolean {
  return cfg.auto_approval?.allowed === true;
}

/**
 * Ставить ли бренду звёздочки и сноску организаций, запрещённых в РФ (`LegalSchema`). Одно
 * правило на писателя, бэкенд и интерфейс: «нет блока» и `false` — одно и то же «нет», и третьего
 * толкования, при котором стороны разметили бы пост по-разному, быть не должно.
 */
export function bannedOrgMarksOn(cfg: Pick<BrandConfig, 'legal'>): boolean {
  return cfg.legal?.banned_orgs === true;
}

/**
 * Включено ли бренду «Добавить мнение» и с каким потолком сырого текста
 * (`generation.author_opinion`). `null` — выключено: маршрут отвечает 409, полосы мнения нет.
 */
export function authorOpinionOf(
  cfg: Pick<BrandConfig, 'generation'>,
): { readonly max_codepoints: number } | null {
  return cfg.generation?.author_opinion ?? null;
}

/**
 * Выпуски из списка наблюдения, у которых сейчас сезон: местный месяц момента `nowMs` в зоне
 * бренда (`publishing.timezone`) стоит в их `months`. Одно правило на расписание мониторинга и
 * на проверки бэкенда; без блока `event_watch` — пусто. Порядок — порядок списка в конфиге.
 */
export function eventsInSeason(
  cfg: Pick<BrandConfig, 'event_watch' | 'publishing'>,
  nowMs: number,
): readonly EventWatchEvent[] {
  const watch = cfg.event_watch;
  if (watch === undefined) return [];
  const month = Number(dayKeyOf(nowMs, cfg.publishing.timezone).slice(5, 7));
  return watch.events.filter((e) => e.months.includes(month));
}

/**
 * Выпуск по коду из списка наблюдения бренда, или `null`. Им бэкенд отбивает тему обзора с
 * выпуском, которого бренд не наблюдает (422), — проверка по конфигу, а не по слову монитора.
 */
export function watchedEventOf(
  cfg: Pick<BrandConfig, 'event_watch'>,
  eventId: string,
): EventWatchEvent | null {
  return cfg.event_watch?.events.find((e) => e.id === eventId) ?? null;
}

/** Сегодняшний обход — последовательный: умолчание `monitoring.crawl_concurrency`. */
export const CRAWL_CONCURRENCY_DEFAULT = 1;

/** Сколько источников обходить одновременно (`monitoring.crawl_concurrency`, умолчание 1). */
export function crawlConcurrencyOf(cfg: Pick<BrandConfig, 'monitoring'>): number {
  return cfg.monitoring?.crawl_concurrency ?? CRAWL_CONCURRENCY_DEFAULT;
}

/**
 * Потолок прогона по расписанию, мс (`monitoring.scheduled_run_timeout_minutes`). `null` — ключа
 * нет: прогон живёт под потолком кнопки, который знает сам мониторинг (`MANUAL_RUN_TIMEOUT_MS`).
 */
export function scheduledRunTimeoutMsOf(cfg: Pick<BrandConfig, 'monitoring'>): number | null {
  const minutes = cfg.monitoring?.scheduled_run_timeout_minutes;
  return minutes === undefined ? null : minutes * 60_000;
}

/** Не отчитывать ли суждённых, но не выбранных кандидатов (`monitoring.rejudge_unselected`). */
export function rejudgeUnselectedOf(cfg: Pick<BrandConfig, 'monitoring'>): boolean {
  return cfg.monitoring?.rejudge_unselected === true;
}

/**
 * Потолки отбора новостного прогона (`monitoring.candidates`); `null` — блока нет, действуют
 * константы мониторинга. Тройка возвращается целиком: подбирается она вместе.
 */
export function newsCandidateCapsOf(cfg: Pick<BrandConfig, 'monitoring'>): {
  readonly max: number;
  readonly per_source: number;
  readonly deep: number;
} | null {
  return cfg.monitoring?.candidates ?? null;
}

/**
 * ПРАВИЛО 6.6 как предупреждение (P2, ревизия 2026-10-18): сквозной бюджет задержки
 * `max_gap(monitoring.schedule) + approval_sla + max_slot_wait` против
 * `freshness.max_age_at_publish_hours`. Отказом загрузки оно было до 19.10.2026; теперь —
 * текст проблемы для журнала старта бэкенда или `null`. Бренд без мониторинга, без слотов,
 * без секции свежести или с нечитаемым расписанием — `null`: те случаи схема отбивает сама.
 */
export function latencyBudgetProblem(cfg: BrandConfig): string | null {
  const mon = cfg.monitoring;
  if (mon === undefined || cfg.freshness === undefined || cfg.publishing.slots.length === 0) {
    return null;
  }
  const tz = cfg.publishing.timezone;
  let maxGapMs: number;
  let maxSlotWaitMs: number;
  try {
    maxGapMs = simulateCronMaxGap(parseCron(mon.schedule), tz).maxGapMs;
    maxSlotWaitMs = simulateSlotsMaxGap(cfg.publishing.slots, tz).maxGapMs;
  } catch {
    return null;
  }
  const slaMs = mon.approval_sla_hours * HOUR_MS;
  const maxAgeMs = cfg.freshness.max_age_at_publish_hours * HOUR_MS;
  const budgetMs = maxGapMs + slaMs + maxSlotWaitMs;
  if (budgetMs <= maxAgeMs) return null;
  const h = (ms: number): string => (ms / HOUR_MS).toFixed(1);
  return (
    `${cfg.id}: сквозной бюджет задержки пробит (ПРАВИЛО 6.6, с 19.10.2026 — предупреждение): ` +
    `max_gap ${h(maxGapMs)} ч + approval_sla ${h(slaMs)} ч + max_slot_wait ${h(maxSlotWaitMs)} ч = ` +
    `${h(budgetMs)} ч > freshness.max_age_at_publish ${h(maxAgeMs)} ч. Возраст при публикации ` +
    'не проверяется — материал старше предела может дойти до канала'
  );
}

/**
 * Наибольший разрыв между прогонами мониторинга бренда, который бренд ОБЕЩАЕТ (P2, находка 60):
 * больший из разрыва `monitoring.schedule` и разрыва дней `auto_generation` (их назначенные
 * минуты — те же «слоты» недели). Таймер `smm-monitor` на проде выключен, и прогоны идут по
 * автоматике пн/ср/пт: обещание по суточному cron давало бы `run_overdue` каждые выходные.
 * По нему мониторинг пишет обещание следующего прогона (`next_expected_at`), а бэкенд —
 * период ожидания `monitor.run.<бренд>`: одно число, одно место. `null` — мониторинга нет или
 * расписание не разбирается (схема такой конфиг не пропускает).
 */
export function monitorRunMaxGapMs(cfg: BrandConfig): number | null {
  const mon = cfg.monitoring;
  if (mon === undefined) return null;
  const tz = cfg.publishing.timezone;
  let gap: number;
  try {
    gap = simulateCronMaxGap(parseCron(mon.schedule), tz).maxGapMs;
  } catch {
    return null;
  }
  const auto = cfg.auto_generation;
  if (auto === undefined) return gap;
  const slots: PublishingSlot[] = [];
  for (const kind of AUTO_GENERATION_KINDS) {
    const job = auto[kind];
    if (job === undefined) continue;
    for (const weekday of job.days) slots.push({ weekday, time: job.time });
  }
  if (slots.length === 0) return gap;
  return Math.max(gap, simulateSlotsMaxGap(slots, tz).maxGapMs);
}
